import { useState, useMemo } from "react"
import { usePageSEO } from "@/lib/usePageSEO"
import { Link } from "react-router-dom"
import { Search, Tag, Calendar, User, Clock, CheckCircle2 } from "lucide-react"

import { APP_NAME } from "@/lib/appConfig"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

import { blogs } from "./content"

export default function BlogListPage() {
    const [searchTerm, setSearchTerm] = useState("")
    const [activeTag, setActiveTag] = useState<string>("All")

    const allTags = useMemo(() => {
        const tags = new Set<string>(["All"])
        blogs.forEach(b => b.tags.forEach(t => tags.add(t)))
        return Array.from(tags)
    }, [])

    const filteredBlogs = useMemo(() => {
        return blogs.filter((blog) => {
            const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
            const matchesTag = activeTag === "All" || blog.tags.includes(activeTag)
            return matchesSearch && matchesTag
        }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }, [searchTerm, activeTag])

    usePageSEO({
        title: `Blog | ${APP_NAME}`,
        description: `Insights, guides, and tips for independent landlords. Learn property management, tenant screening, finances, and more.`,
        path: "/blog",
        jsonLd: [
            {
                "@type": "Blog",
                name: `${APP_NAME} Blog`,
                description: "Guides and tips for independent landlords.",
                url: "https://rent-keep.com/blog",
                blogPost: blogs.map((p) => ({
                    "@type": "BlogPosting",
                    headline: p.title,
                    description: p.excerpt,
                    datePublished: p.date,
                    author: { "@type": "Person", name: p.author },
                    url: `https://rent-keep.com/blog/${p.slug}`,
                })),
            },
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://rent-keep.com" },
                    { "@type": "ListItem", position: 2, name: "Blog", item: "https://rent-keep.com/blog" },
                ],
            },
        ],
    })

    return (
        <div className="w-full bg-background flex flex-col">
            {/* Simple Hero Section instead of PageHeader */}
            <div className="w-full bg-muted/30 border-b border-border/50 py-12 px-4 text-center">
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">{APP_NAME} Blog</h1>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-medium">Insights, guides, and tips for modern property management</p>
            </div>

            <main className="flex-1 w-full max-w-[1200px] mx-auto px-4 py-8">
                {/* Search & Filter Header */}
                <div className="flex flex-col md:flex-row gap-6 mb-12 items-start md:items-center justify-between">
                    <div className="relative w-full md:w-[400px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            placeholder="Search articles..."
                            className="pl-12 h-14 rounded-2xl bg-card/60 backdrop-blur-xl border-border/50 text-base shadow-soft"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none snap-x mask-fade-edges">
                        {allTags.map(tag => (
                            <button
                                key={tag}
                                onClick={() => setActiveTag(tag)}
                                className={`shrink-0 snap-center h-10 px-6 rounded-full text-sm font-bold transition-all shadow-sm ${activeTag === tag
                                    ? "bg-primary text-primary-foreground shadow-glow scale-105"
                                    : "bg-card/50 backdrop-blur-md border border-border/50 text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                                    }`}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Blog Grid */}
                {filteredBlogs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredBlogs.map((blog) => (
                            <Link key={blog.slug} to={`/blog/${blog.slug}`} className="group outline-none">
                                <Card className="h-full flex flex-col rounded-3xl overflow-hidden border-border/50 bg-card/60 backdrop-blur-xl shadow-soft group-hover:shadow-xl transition-all duration-500 group-hover:-translate-y-2">
                                    <div className="w-full aspect-video bg-muted relative overflow-hidden">
                                        {blog.coverImage ? (
                                            <img
                                                src={blog.coverImage}
                                                alt={blog.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                                                <CheckCircle2 className="h-12 w-12 text-primary/30" />
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4 flex gap-2">
                                            {blog.tags.slice(0, 2).map(tag => (
                                                <Badge key={tag} className="bg-background/80 backdrop-blur text-foreground border-none font-bold uppercase text-[10px] tracking-wider">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>

                                    <CardContent className="p-6 flex-1 flex flex-col">
                                        <h2 className="text-xl font-extrabold tracking-tight text-foreground group-hover:text-primary transition-colors leading-tight mb-3 line-clamp-2">
                                            {blog.title}
                                        </h2>
                                        <p className="text-muted-foreground text-sm leading-relaxed mb-6 line-clamp-3 flex-1 font-medium">
                                            {blog.excerpt}
                                        </p>

                                        <div className="flex items-center gap-4 text-xs font-semibold text-muted-foreground mt-auto">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className="h-3.5 w-3.5" />
                                                {new Date(blog.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="h-3.5 w-3.5" />
                                                {blog.readTime}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-24 flex flex-col items-center justify-center opacity-70">
                        <Tag className="h-16 w-16 text-muted-foreground mb-6" />
                        <h3 className="text-2xl font-bold tracking-tight mb-2">No articles found</h3>
                        <p className="text-muted-foreground font-medium">Try adjusting your search or filter criteria.</p>
                    </div>
                )}
            </main>
        </div>
    )
}
